import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll } from "framer-motion";
import { useParams } from "react-router-dom";
import responseService from "../services/responseService";
import questionnaireService from "../services/questionnaireService";
import { SECTION_TYPES } from "../models/types";
import SoundboardSection from "../../Soundboard/Soundboard";
import DeliverySection from "../../BrandProfile/DeliverySection";

// Import dynamically created section components using the index file
import {
  DynamicBrandInfoSection,
  DynamicBrandCharacteristicsSection,
  DynamicBrandBackgroundSection,
  DynamicBrandPersonalitySection,
  DynamicBrandStrategySection,
  DynamicDemographicsSection,
  DynamicBrandSoundSection,
  DynamicProjectGoalsSection,
  DynamicBrandVideosSection,
} from "./sections";

import DynamicSoundboardSection from "./DynamicSoundboardSection";
import DynamicDeliverySection from "./DynamicDeliverySection";

const DynamicProfileVisualizer = ({ projectId, responseId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionnaireData, setQuestionnaireData] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const [activeTab, setActiveTab] = useState("discovery");

  // State for brand images
  const [brandImages, setBrandImages] = useState({
    logo: null,
    hero: [],
    other: [],
    soundboard: [],
  });

  // State for active hero image
  const [activeHeroImage, setActiveHeroImage] = useState(null);

  // State to store pre-calculated background images for sections
  const [sectionBackgroundImages, setSectionBackgroundImages] = useState({});

  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll();

  // Color palette for the profile
  const [palette, setPalette] = useState({
    primary: "#7C3AED", // hearmeoutmusic brand purple
    secondary: "#f25022",
    accent: "#ffb900",
    background: "#0a101e",
    text: "#ffffff",
  });

  // Header hide/show on scroll for soundboard/delivery
  const [showHeader, setShowHeader] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("[DynamicProfileVisualizer] Fetching data with:", {
          projectId,
          responseId,
        });

        // Get response data - either by ID or latest for project
        let response;
        if (responseId) {
          console.log(
            "[DynamicProfileVisualizer] Fetching response by ID:",
            responseId
          );
          response = await responseService.getResponseById(responseId);
        } else if (projectId) {
          console.log(
            "[DynamicProfileVisualizer] Fetching latest response for project:",
            projectId
          );
          response = await responseService.getLatestResponseForProject(
            projectId
          );
        } else {
          throw new Error("Either projectId or responseId must be provided");
        }

        if (!response) {
          console.log("[DynamicProfileVisualizer] No response found");
          throw new Error("No questionnaire response found");
        }

        console.log("[DynamicProfileVisualizer] Response data:", response);
        setResponseData(response);

        // Get questionnaire structure
        console.log(
          "[DynamicProfileVisualizer] Fetching questionnaire ID:",
          response.questionnaireId
        );
        const questionnaire = await questionnaireService.getQuestionnaireById(
          response.questionnaireId
        );
        console.log(
          "[DynamicProfileVisualizer] Questionnaire data:",
          questionnaire
        );
        setQuestionnaireData(questionnaire);

        // Create a mapping from question titles to question IDs for custom title lookup
        const titleToIdMap = {};
        if (questionnaire && questionnaire.sections) {
          questionnaire.sections.forEach((section) => {
            if (section.questions) {
              section.questions.forEach((question) => {
                titleToIdMap[question.title] = question.id;
              });
            }
          });
        }

        // Store the mapping for use in sections
        if (response.metadata?.questionTitles) {
          // Create a reverse mapping: trait name -> custom title
          const traitToCustomTitle = {};
          Object.entries(response.metadata.questionTitles).forEach(
            ([questionId, customTitle]) => {
              // Find question title for this ID
              const questionTitle = Object.keys(titleToIdMap).find(
                (title) => titleToIdMap[title] === questionId
              );
              if (questionTitle) {
                traitToCustomTitle[questionTitle] = customTitle;
              }
            }
          );
          // Store in responseData for easy access
          response.metadata.traitToCustomTitle = traitToCustomTitle;
        }

        // Transform response data for visualization
        console.log("[DynamicProfileVisualizer] Transforming response data");
        const transformedData =
          responseService.transformResponseForBrandProfile(
            response,
            response.questionnaireSnapshot || questionnaire
          );
        console.log(
          "[DynamicProfileVisualizer] Transformed data:",
          transformedData
        );
        setProfileData(transformedData);

        // Set the profileId for narratives - use response ID to match where narratives are stored
        setProfileId(response.id);

        // Apply default tab if specified, but only if that tab is visible
        const configuredDefaultTab = response.visibility?.defaultTab;
        const availableTabs = [];

        // Check which tabs are available
        if (!(response.visibility?.pages?.discovery === false))
          availableTabs.push("discovery");
        if (!(response.visibility?.pages?.soundboard === false))
          availableTabs.push("soundboard");
        if (!(response.visibility?.pages?.delivery === false))
          availableTabs.push("delivery");

        // Set default tab - use configured tab if available, otherwise use first available tab
        if (
          configuredDefaultTab &&
          availableTabs.includes(configuredDefaultTab)
        ) {
          setActiveTab(configuredDefaultTab);
        } else if (availableTabs.length > 0) {
          setActiveTab(availableTabs[0]);
        }

        // Extract colors if available in the response
        if (
          transformedData.brandInfo &&
          transformedData.brandInfo["Brand Colors"]
        ) {
          const colors = transformedData.brandInfo["Brand Colors"];
          console.log("[DynamicProfileVisualizer] Extracted colors:", colors);
          if (colors) {
            setPalette((prevPalette) => ({
              ...prevPalette,
              primary: colors.primary || prevPalette.primary,
              secondary: colors.secondary || prevPalette.secondary,
              accent: colors.accent || prevPalette.accent,
            }));
          }
        }

        // Theme override from metadata
        if (response.metadata && response.metadata.theme) {
          const t = response.metadata.theme;
          setPalette((prev) => ({
            ...prev,
            primary: t.primary || prev.primary,
            secondary: t.secondary || prev.secondary,
            accent: t.accent || prev.accent,
          }));
        }

        // Extract brand images from response metadata or from separate field
        // First, check if there's a brandStyle field with images
        if (
          response.brandStyle &&
          response.brandStyle.images &&
          response.brandStyle.images.length > 0
        ) {
          const images = response.brandStyle.images;

          // Sort images by type, preserving full image objects for hero images
          const sortedImages = {
            logo: images.find((img) => img.type === "logo")?.url || null,
            hero: images.filter((img) => img.type === "hero"), // Keep full objects with IDs
            other: images
              .filter((img) => img.type === "other")
              .map((img) => img.url),
            soundboard: images
              .filter((img) => img.type === "soundboard")
              .map((img) => img.url),
          };

          console.log(
            "[DynamicProfileVisualizer] Extracted images:",
            sortedImages
          );
          // ADD DEBUG: Log specifically the "other" images array
          console.log(
            "[DynamicProfileVisualizer] DEBUG - Other/Section Background Images:",
            sortedImages.other
          );
          console.log(
            "[DynamicProfileVisualizer] DEBUG - Number of section background images:",
            sortedImages.other.length
          );
          setBrandImages(sortedImages);

          // Extract active hero image from metadata
          const activeHeroImageId = response.metadata?.activeHeroImageId;
          console.log(
            "[DynamicProfileVisualizer] Active hero image ID from metadata:",
            activeHeroImageId
          );
          console.log(
            "[DynamicProfileVisualizer] Available hero images:",
            sortedImages.hero
          );

          if (activeHeroImageId && sortedImages.hero.length > 0) {
            const activeHero = sortedImages.hero.find(
              (img) => img.id === activeHeroImageId
            );
            if (activeHero) {
              setActiveHeroImage(activeHero.url);
              console.log(
                "[DynamicProfileVisualizer] Set active hero image:",
                activeHero.url
              );
            } else {
              // Fallback to first hero image if active ID not found
              setActiveHeroImage(sortedImages.hero[0]?.url || null);
              console.log(
                "[DynamicProfileVisualizer] Active hero ID not found, using first hero image:",
                sortedImages.hero[0]?.url
              );
            }
          } else if (sortedImages.hero.length > 0) {
            // Fallback to first hero image if no active ID specified
            setActiveHeroImage(sortedImages.hero[0]?.url || null);
            console.log(
              "[DynamicProfileVisualizer] No active hero ID, using first hero image:",
              sortedImages.hero[0]?.url
            );
          } else {
            console.log("[DynamicProfileVisualizer] No hero images available");
          }
        }
        // Fallback to check metadata for images
        else if (response.metadata && response.metadata.brandImages) {
          setBrandImages(response.metadata.brandImages);
          console.log("[DynamicProfileVisualizer] Using metadata images");

          // For legacy metadata format, try to use first hero image
          if (
            response.metadata.brandImages.hero &&
            response.metadata.brandImages.hero.length > 0
          ) {
            setActiveHeroImage(response.metadata.brandImages.hero[0]);
          }
        } else {
          console.log("[DynamicProfileVisualizer] No brand images found");
        }

        setLoading(false);
      } catch (err) {
        console.error("[DynamicProfileVisualizer] Error fetching data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, responseId]);

  // Generate section background images when brand images change
  useEffect(() => {
    const newSectionImages = {};

    // Generate images for discovery sections
    if (brandImages.other && brandImages.other.length > 0) {
      // ADD DEBUG: Log what we're working with
      console.log(
        "[DynamicProfileVisualizer] DEBUG - About to generate section backgrounds from:",
        brandImages.other
      );
      const discoveryImages = generateSectionBackgroundImages(
        brandImages.other
      );
      Object.assign(newSectionImages, discoveryImages);
      // ADD DEBUG: Log what was generated
      console.log(
        "[DynamicProfileVisualizer] DEBUG - Generated section images:",
        discoveryImages
      );
    } else {
      console.log(
        "[DynamicProfileVisualizer] DEBUG - No 'other' images found or array is empty:",
        { hasOther: !!brandImages.other, length: brandImages.other?.length }
      );
    }

    // Handle soundboard section - prefer soundboard images, fallback to other images
    const soundboardImageCollection =
      brandImages.soundboard && brandImages.soundboard.length > 0
        ? brandImages.soundboard
        : brandImages.other;

    if (soundboardImageCollection && soundboardImageCollection.length > 0) {
      // Get a random image for soundboard that's different from the last discovery section
      const lastDiscoveryImage = newSectionImages.projectGoals;
      let availableImages = soundboardImageCollection;

      if (lastDiscoveryImage && availableImages.length > 1) {
        availableImages = soundboardImageCollection.filter(
          (img) => img !== lastDiscoveryImage
        );
        if (availableImages.length === 0) {
          availableImages = soundboardImageCollection;
        }
      }

      const randomIndex = Math.floor(Math.random() * availableImages.length);
      newSectionImages.soundboard = availableImages[randomIndex];
    }

    setSectionBackgroundImages(newSectionImages);
  }, [brandImages.other, brandImages.soundboard]);

  // Apply the color palette to the document
  useEffect(() => {
    if (palette) {
      document.documentElement.style.setProperty(
        "--primary-color",
        palette.primary
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        palette.secondary
      );
      document.documentElement.style.setProperty(
        "--accent-color",
        palette.accent
      );

      // Convert hex to RGB for gradients and effects
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
              result[3],
              16
            )}`
          : null;
      };

      document.documentElement.style.setProperty(
        "--primary-color-rgb",
        hexToRgb(palette.primary)
      );
      document.documentElement.style.setProperty(
        "--secondary-color-rgb",
        hexToRgb(palette.secondary)
      );
      document.documentElement.style.setProperty(
        "--accent-color-rgb",
        hexToRgb(palette.accent)
      );
    }
  }, [palette]);

  // Tab navigation UI component - moved to a standalone function
  const TabNavigation = () => {
    const showSoundboard = !(
      responseData?.visibility?.pages &&
      responseData.visibility.pages.soundboard === false
    );
    const showDelivery = !(
      responseData?.visibility?.pages &&
      responseData.visibility.pages.delivery === false
    );
    const showDiscovery = !(
      responseData?.visibility?.pages &&
      responseData.visibility.pages.discovery === false
    );

    // Calculate the number of visible tabs for proper sizing
    const visibleTabs = [showDiscovery, showSoundboard, showDelivery].filter(
      Boolean
    ).length;
    const tabWidth = visibleTabs > 0 ? `${100 / visibleTabs}%` : "33.333%";

    // Calculate the position based on active tab
    const getTabPosition = () => {
      const tabs = [];
      if (showDiscovery) tabs.push("discovery");
      if (showSoundboard) tabs.push("soundboard");
      if (showDelivery) tabs.push("delivery");

      return tabs.indexOf(activeTab);
    };

    const tabPosition = getTabPosition();

    return (
      <motion.div
        className="inline-flex items-center bg-black/40 backdrop-blur-md rounded-full p-1.5 border border-white/10 shadow-lg relative overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        style={{
          boxShadow:
            "0 4px 20px rgba(0, 0, 0, 0.3), 0 0 15px rgba(var(--primary-color-rgb), 0.2)",
        }}
      >
        {/* Animated background for selected tab */}
        <motion.div
          className="absolute h-full rounded-full z-0"
          animate={{
            x: `${tabPosition * 100}%`,
            width: tabWidth,
          }}
          transition={{ type: "spring", stiffness: 300, damping: 30, mass: 1 }}
          style={{
            background: `linear-gradient(135deg, var(--primary-color-20) 0%, var(--primary-color) 100%)`,
            top: 0,
            left: 0,
          }}
        />
        {showDiscovery && (
          <button
            className={`px-6 py-3 rounded-full text-base md:text-lg font-medium transition-colors duration-200 relative z-10 ${
              activeTab === "discovery"
                ? "text-white"
                : "text-white/60 hover:text-white"
            }`}
            onClick={() => setActiveTab("discovery")}
          >
            <span>Discovery</span>
          </button>
        )}
        {showSoundboard && (
          <button
            className={`px-6 py-3 rounded-full text-base md:text-lg font-medium transition-colors duration-200 relative z-10 ${
              activeTab === "soundboard"
                ? "text-white"
                : "text-white/60 hover:text-white"
            }`}
            onClick={() => setActiveTab("soundboard")}
          >
            <span>Sound Board</span>
          </button>
        )}
        {showDelivery && (
          <button
            className={`px-6 py-3 rounded-full text-base md:text-lg font-medium transition-colors duration-200 relative z-10 ${
              activeTab === "delivery"
                ? "text-white"
                : "text-white/60 hover:text-white"
            }`}
            onClick={() => setActiveTab("delivery")}
          >
            <span>Delivery</span>
          </button>
        )}
      </motion.div>
    );
  };

  // Helper function to get a random image from a collection
  const getRandomImage = (imageCollection) => {
    if (!imageCollection || imageCollection.length === 0) return null;

    // Handle hero images which are objects with url property
    if (Array.isArray(imageCollection) && imageCollection[0]?.url) {
      const randomIndex = Math.floor(Math.random() * imageCollection.length);
      return imageCollection[randomIndex].url;
    }

    // Handle other image types which are just URL strings
    return imageCollection[Math.floor(Math.random() * imageCollection.length)];
  };

  // Helper function to generate non-repeating background images for sections
  const generateSectionBackgroundImages = (imageCollection) => {
    if (!imageCollection || imageCollection.length === 0) return {};

    // Handle hero images which are objects with url property
    let availableImages = imageCollection;
    if (Array.isArray(imageCollection) && imageCollection[0]?.url) {
      availableImages = imageCollection.map((img) => img.url);
    }

    if (availableImages.length === 0) return {};

    // Define the sections that need background images in order
    const sections = [
      "brandBackground",
      "brandCharacteristics",
      "demographics",
      "brandStrategy",
      "brandPersonality",
      "brandSound",
      "projectGoals",
      "brandVideos",
      "soundboard",
      "delivery",
    ];

    const sectionImages = {};
    let lastUsedImage = null;

    sections.forEach((sectionKey) => {
      if (availableImages.length === 1) {
        // Only one image available, use it
        sectionImages[sectionKey] = availableImages[0];
      } else {
        // Filter out the last used image if we have more than one option
        let filteredImages = availableImages;
        if (lastUsedImage && availableImages.length > 1) {
          filteredImages = availableImages.filter(
            (img) => img !== lastUsedImage
          );

          // If filtering removed all images, fall back to original array
          if (filteredImages.length === 0) {
            filteredImages = availableImages;
          }
        }

        // Select a random image from the filtered array
        const randomIndex = Math.floor(Math.random() * filteredImages.length);
        const selectedImage = filteredImages[randomIndex];

        sectionImages[sectionKey] = selectedImage;
        lastUsedImage = selectedImage;
      }
    });

    console.log(
      "[DynamicProfileVisualizer] Generated section background images:",
      sectionImages
    );
    return sectionImages;
  };

  // Header hide/show on scroll for soundboard/delivery
  useEffect(() => {
    if (activeTab === "soundboard" || activeTab === "delivery") {
      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        if (!ticking.current) {
          window.requestAnimationFrame(() => {
            if (currentScrollY <= 0) {
              setShowHeader(true);
            } else if (currentScrollY > lastScrollY.current) {
              setShowHeader(false); // scrolling down
            } else {
              setShowHeader(true); // scrolling up
            }
            lastScrollY.current = currentScrollY;
            ticking.current = false;
          });
          ticking.current = true;
        }
      };
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    } else {
      setShowHeader(true); // Always show header in discovery
    }
  }, [activeTab]);

  const isSectionVisible = (id) =>
    !(
      responseData?.visibility?.sections &&
      responseData.visibility.sections[id] === false
    );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen w-full">
        <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen w-full">
        <div className="bg-red-500/10 p-6 rounded-xl border border-red-500/20">
          <h2 className="text-xl font-bold text-red-500 mb-2">Error</h2>
          <p className="text-white/70">{error}</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="flex justify-center items-center min-h-screen w-full">
        <div className="bg-blue-500/10 p-6 rounded-xl border border-blue-500/20">
          <h2 className="text-xl font-bold text-blue-500 mb-2">No Data</h2>
          <p className="text-white/70">
            No questionnaire responses found for this project.
          </p>
        </div>
      </div>
    );
  }

  // Main content rendering based on tab
  const renderContent = () => {
    if (activeTab === "discovery") {
      const discVisible = !(
        responseData?.visibility?.pages &&
        responseData.visibility.pages.discovery === false
      );
      if (!discVisible) return null; // only hero already rendered
      let sectionIndex = 0;
      return (
        <>
          {/* Render all other sections as they're available in the data */}
          {profileData.brandBackground &&
            isSectionVisible("brandBackground") && (
              <DynamicBrandBackgroundSection
                background={profileData.brandBackground}
                scrollProgress={scrollYProgress}
                palette={palette}
                profileId={profileId}
                backgroundImage={sectionBackgroundImages.brandBackground}
                sectionIndex={sectionIndex++}
                questionTitles={responseData?.metadata?.traitToCustomTitle}
                responseData={responseData}
                questionnaireData={questionnaireData}
              />
            )}
          {profileData.brandCharacteristics &&
            isSectionVisible("brandCharacteristics") && (
              <DynamicBrandCharacteristicsSection
                characteristics={profileData.brandCharacteristics}
                scrollProgress={scrollYProgress}
                palette={palette}
                profileId={profileId}
                backgroundImage={sectionBackgroundImages.brandCharacteristics}
                sectionIndex={sectionIndex++}
                questionTitles={responseData?.metadata?.traitToCustomTitle}
                responseData={responseData}
                questionnaireData={questionnaireData}
              />
            )}
          {profileData.demographics && isSectionVisible("demographics") && (
            <DynamicDemographicsSection
              demographics={profileData.demographics}
              scrollProgress={scrollYProgress}
              palette={palette}
              profileId={profileId}
              backgroundImage={sectionBackgroundImages.demographics}
              sectionIndex={sectionIndex++}
              questionTitles={responseData?.metadata?.traitToCustomTitle}
              responseData={responseData}
              questionnaireData={questionnaireData}
            />
          )}
          {profileData.brandStrategy && isSectionVisible("brandStrategy") && (
            <DynamicBrandStrategySection
              strategy={profileData.brandStrategy}
              scrollProgress={scrollYProgress}
              palette={palette}
              profileId={profileId}
              backgroundImage={sectionBackgroundImages.brandStrategy}
              sectionIndex={sectionIndex++}
              questionTitles={responseData?.metadata?.traitToCustomTitle}
              responseData={responseData}
              questionnaireData={questionnaireData}
            />
          )}
          {profileData.brandPersonality &&
            isSectionVisible("brandPersonality") && (
              <DynamicBrandPersonalitySection
                personality={profileData.brandPersonality}
                scrollProgress={scrollYProgress}
                palette={palette}
                profileId={profileId}
                backgroundImage={sectionBackgroundImages.brandPersonality}
                sectionIndex={sectionIndex++}
                questionTitles={responseData?.metadata?.traitToCustomTitle}
                responseData={responseData}
                questionnaireData={questionnaireData}
              />
            )}
          {profileData.brandSound && isSectionVisible("brandSound") && (
            <DynamicBrandSoundSection
              sound={profileData.brandSound}
              scrollProgress={scrollYProgress}
              palette={palette}
              profileId={profileId}
              backgroundImage={sectionBackgroundImages.brandSound}
              sectionIndex={sectionIndex++}
              questionTitles={responseData?.metadata?.traitToCustomTitle}
              responseData={responseData}
              questionnaireData={questionnaireData}
            />
          )}
          {profileData.projectGoals && isSectionVisible("projectGoals") && (
            <DynamicProjectGoalsSection
              goals={profileData.projectGoals}
              scrollProgress={scrollYProgress}
              palette={palette}
              profileId={profileId}
              backgroundImage={sectionBackgroundImages.projectGoals}
              sectionIndex={sectionIndex++}
              questionTitles={responseData?.metadata?.traitToCustomTitle}
              responseData={responseData}
              questionnaireData={questionnaireData}
            />
          )}
          {profileData.brandVideos && isSectionVisible("brandVideos") && (
            <DynamicBrandVideosSection
              videos={profileData.brandVideos}
              scrollProgress={scrollYProgress}
              palette={palette}
              profileId={profileId}
              backgroundImage={sectionBackgroundImages.brandVideos}
              sectionIndex={sectionIndex++}
              title="Brand Videos"
              description="Explore our visual stories and brand moments"
              showCategories={true}
              maxColumns={3}
              enableModal={true}
            />
          )}
        </>
      );
    } else if (activeTab === "soundboard") {
      return (
        <DynamicSoundboardSection
          description={profileData.soundboard?.description || ""}
          timeline={profileData.soundboard?.timeline || []}
          audioFiles={profileData.soundboard?.audioFiles || []}
          notes={profileData.soundboard?.notes || []}
          optimizedContent={responseData?.metadata?.optimizedContent || {}}
          backgroundImage={sectionBackgroundImages.soundboard}
        />
      );
    } else if (activeTab === "delivery") {
      return (
        <DynamicDeliverySection
          sections={profileData.deliverySections || []}
          optimizedContent={responseData?.metadata?.optimizedContent || {}}
          backgroundImage={sectionBackgroundImages.delivery}
          deliveryDriveUrl={responseData?.metadata?.deliveryDriveUrl || ""}
        />
      );
    }
    return null;
  };

  return (
    <div
      ref={containerRef}
      className="dynamic-profile-visualizer bg-gradient-to-b from-background to-background/90 min-h-screen w-full text-white"
    >
      {/* Header: Only animate/hide on soundboard/delivery, always show in discovery/brand info */}
      {(activeTab === "soundboard" || activeTab === "delivery") &&
        profileData.brandInfo && (
          <motion.div
            initial={{ y: 0 }}
            animate={{ y: showHeader ? 0 : "-110%" }}
            transition={{ type: "spring", stiffness: 300, damping: 35 }}
            className="fixed top-0 left-0 w-full z-50"
            style={{ pointerEvents: showHeader ? "auto" : "none" }}
          >
            <DynamicBrandInfoSection
              brandInfo={profileData.brandInfo}
              palette={palette}
              scrollProgress={scrollYProgress}
              profileId={profileId}
              tabNavigation={<TabNavigation />}
              activeTab={activeTab}
              backgroundImage={activeHeroImage}
              logoUrl={brandImages.logo}
            />
          </motion.div>
        )}
      {/* Keep brand info section at the top regardless of which tab is active (discovery) */}
      {activeTab === "discovery" && profileData.brandInfo && (
        <DynamicBrandInfoSection
          brandInfo={profileData.brandInfo}
          palette={palette}
          scrollProgress={scrollYProgress}
          profileId={profileId}
          tabNavigation={<TabNavigation />}
          activeTab={activeTab}
          backgroundImage={activeHeroImage}
          logoUrl={brandImages.logo}
        />
      )}
      {/* Fallback for no brand info */}
      {!profileData.brandInfo && (
        <div className="min-h-screen flex flex-col justify-center items-center w-full">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 text-white text-center"
          >
            Brand Profile
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white/70 text-center max-w-2xl px-4"
          >
            This brand profile has been generated from your questionnaire
            responses. Scroll down to explore the details.
          </motion.p>
          {/* Add tab navigation here as well for the fallback case */}
          <div className="mt-8">
            <TabNavigation />
          </div>
        </div>
      )}

      {/* Create space for the sticky header when in non-discovery tabs */}
      {activeTab !== "discovery" && <div className="pt-24"></div>}

      {/* Render the content based on active tab */}
      <div className={activeTab === "discovery" ? "" : "pt-4"}>
        {renderContent()}
      </div>

      {/* Footer section with data metadata */}
      <motion.footer
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-12 px-4 text-center text-white/40 text-sm w-full"
      >
        <p>Generated from questionnaire response {responseData?.id}</p>
        <p className="mt-2">
          Last updated:{" "}
          {responseData?.submittedAt
            ? new Date(responseData.submittedAt.seconds * 1000).toLocaleString()
            : "Not submitted"}
        </p>
      </motion.footer>
    </div>
  );
};

export default DynamicProfileVisualizer;
